import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Camera, Check, AlertCircle } from 'lucide-react';
import { userService } from '../features/auth/services/userService';

const UpdateProfilePhoto = ({ isOpen, onClose, onSuccess }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];

        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                return;
            }

            setSelectedFile(file);
            setError(null);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const response = await userService.uploadProfilePicture(selectedFile);

            console.log('Upload Response:', response);
            console.log('Profile Picture URL:', response.profile_picture);

            if (response.status) {
                setSuccess(true);

                // Update user data in localStorage
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    const userData = JSON.parse(storedUser);
                    // Set both fields to ensure compatibility
                    userData.picture = response.profile_picture;
                    userData.profile_picture = response.profile_picture;
                    localStorage.setItem('user', JSON.stringify(userData));
                    console.log('Updated user data in localStorage:', userData);
                    console.log('New profile picture URL:', response.profile_picture);
                }

                // Call success callback
                if (onSuccess) {
                    onSuccess(response.profile_picture);
                }

                // Close modal after 1.5 seconds
                setTimeout(() => {
                    handleClose();
                }, 1500);
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.message || 'Failed to upload profile picture');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setPreview(null);
        setError(null);
        setSuccess(false);
        onClose();
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}
                onClick={handleClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#111827',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '100%',
                        border: '1px solid #1f2937',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#fff',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Camera size={24} />
                            Update Profile Photo
                        </h2>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#1f2937';
                                e.currentTarget.style.color = '#fff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#9ca3af';
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* File Input (Hidden) */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    {/* Preview Area */}
                    {preview ? (
                        <div style={{
                            marginBottom: '20px',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '3px solid #3b82f6',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                            }}>
                                <img
                                    src={preview}
                                    alt="Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            marginBottom: '20px',
                            padding: '40px',
                            border: '2px dashed #374151',
                            borderRadius: '8px',
                            textAlign: 'center',
                            background: '#0f1419'
                        }}>
                            <Upload size={48} style={{ color: '#6b7280', margin: '0 auto 12px' }} />
                            <p style={{
                                color: '#9ca3af',
                                fontSize: '14px',
                                margin: 0
                            }}>
                                No file selected
                            </p>
                        </div>
                    )}

                    {/* Select File Button */}
                    <button
                        onClick={handleButtonClick}
                        disabled={uploading}
                        style={{
                            width: '100%',
                            padding: '12px 20px',
                            background: preview ? '#1f2937' : '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s',
                            opacity: uploading ? 0.6 : 1
                        }}
                        onMouseOver={(e) => {
                            if (!uploading) {
                                e.currentTarget.style.background = preview ? '#374151' : '#2563eb';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!uploading) {
                                e.currentTarget.style.background = preview ? '#1f2937' : '#3b82f6';
                            }
                        }}
                    >
                        <Upload size={18} />
                        {preview ? 'Change Photo' : 'Select Photo'}
                    </button>

                    {/* Upload Button */}
                    {preview && (
                        <button
                            onClick={handleUpload}
                            disabled={uploading || success}
                            style={{
                                width: '100%',
                                padding: '12px 20px',
                                background: success ? '#22c55e' : '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: uploading || success ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                opacity: uploading ? 0.6 : 1
                            }}
                            onMouseOver={(e) => {
                                if (!uploading && !success) {
                                    e.currentTarget.style.background = '#2563eb';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!uploading && !success) {
                                    e.currentTarget.style.background = '#3b82f6';
                                }
                            }}
                        >
                            {uploading ? (
                                <>
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        border: '2px solid #fff',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                    Uploading...
                                </>
                            ) : success ? (
                                <>
                                    <Check size={18} />
                                    Uploaded Successfully!
                                </>
                            ) : (
                                <>
                                    <Upload size={18} />
                                    Upload Photo
                                </>
                            )}
                        </button>
                    )}

                    {/* Error Message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#ef4444',
                                fontSize: '13px'
                            }}
                        >
                            <AlertCircle size={16} />
                            {error}
                        </motion.div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid #22c55e',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#22c55e',
                                fontSize: '13px'
                            }}
                        >
                            <Check size={16} />
                            Profile picture updated successfully!
                        </motion.div>
                    )}

                    {/* File Info */}
                    {selectedFile && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: '#0f1419',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#9ca3af'
                        }}>
                            <div style={{ marginBottom: '4px' }}>
                                <strong style={{ color: '#e5e7eb' }}>File:</strong> {selectedFile.name}
                            </div>
                            <div>
                                <strong style={{ color: '#e5e7eb' }}>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* CSS Animation for spinner */}
                <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
            </motion.div>
        </AnimatePresence>
    );
};

export default UpdateProfilePhoto;
